// Stamps (issue 1034; design/28 §4a), the client's half: the person's library — the four calls, each to this origin
// through `requestJson` (so `authFetch`) — and one shared copy of the list for the toolbar, loaded once a session and
// kept up to date by this person's own saves, renames and deletes.
import { useEffect, useState } from 'react';
import { parseStamp, type Stamp, type StampMark } from '@dragons-table/vtt-shared';
import { requestJson } from '../api';

type Refused = { kind: 'refused'; reason: string } | { kind: 'error' };
export type StampsAnswer = { kind: 'ok'; stamps: Stamp[] } | Refused;
export type StampAnswer = { kind: 'ok'; stamp: Stamp } | Refused;

const refusedOf = (status: number, body: unknown): Refused => {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return status >= 400 && status < 500 && typeof reason === 'string' ? { kind: 'refused', reason } : { kind: 'error' };
};
const enc = encodeURIComponent;

export async function stampList(fetchImpl: typeof fetch = fetch): Promise<StampsAnswer> {
  const { status, body } = await requestJson('/api/stamps', {}, fetchImpl);
  const list = (body as { stamps?: unknown } | null)?.stamps;
  if (status === 200 && Array.isArray(list)) return { kind: 'ok', stamps: list.map(parseStamp).filter((s): s is Stamp => s !== null) };
  return refusedOf(status, body);
}

const one = (status: number, body: unknown, ok: number): StampAnswer => {
  const stamp = status === ok ? parseStamp(body) : null;
  return stamp ? { kind: 'ok', stamp } : refusedOf(status, body);
};

export async function saveStamp(name: string, marks: StampMark[], fetchImpl: typeof fetch = fetch): Promise<StampAnswer> {
  const { status, body } = await requestJson('/api/stamps', { method: 'POST', body: JSON.stringify({ name, marks }) }, fetchImpl);
  return one(status, body, 201);
}

export async function renameStamp(id: string, name: string, fetchImpl: typeof fetch = fetch): Promise<StampAnswer> {
  const { status, body } = await requestJson(`/api/stamps/${enc(id)}`, { method: 'PUT', body: JSON.stringify({ name }) }, fetchImpl);
  return one(status, body, 200);
}

export async function deleteStamp(id: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const { status } = await requestJson(`/api/stamps/${enc(id)}`, { method: 'DELETE' }, fetchImpl);
  return status === 204;
}

// ── The list, shared by whatever shows it ─────────────────────────────────
export interface Library { stamps: Stamp[]; loaded: boolean; failed: boolean }
let library: Library = { stamps: [], loaded: false, failed: false };
let loading = false;
const listeners = new Set<(l: Library) => void>();
const set = (next: Library) => { library = next; for (const l of listeners) l(library); };

export const stampLibrary = {
  get: () => library,
  async load(force = false): Promise<void> {
    if (loading || (library.loaded && !force)) return;
    loading = true;
    const got = await stampList();
    loading = false;
    set(got.kind === 'ok' ? { stamps: got.stamps, loaded: true, failed: false } : { ...library, loaded: true, failed: true });
  },
  added(s: Stamp): void { set({ ...library, stamps: [...library.stamps.filter((x) => x.id !== s.id), s] }); },
  changed(s: Stamp): void { set({ ...library, stamps: library.stamps.map((x) => (x.id === s.id ? s : x)) }); },
  removed(id: string): void { set({ ...library, stamps: library.stamps.filter((x) => x.id !== id) }); },
  subscribe(l: (lib: Library) => void): () => void { listeners.add(l); return () => { listeners.delete(l); }; },
};

/** The person's library, loaded the first time anything asks for it. */
export function useStamps(): Library {
  const [lib, setLib] = useState(stampLibrary.get());
  useEffect(() => { const off = stampLibrary.subscribe(setLib); void stampLibrary.load(); return off; }, []);
  return lib;
}
